import { Link } from 'expo-router';
import ChevronDown from 'lucide-react-native/icons/chevron-down';
import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { useTheme } from '@/hooks/use-theme';

import type { FooterColumnProps } from './footer-column.types';
import css from './footer-column.module.css';

// Web: phone vs wider layout is decided by footer-column.module.css media
// queries (see the note there). The title is rendered twice — a plain label for
// the column layout and a toggle button for the accordion — and CSS shows
// exactly one, so neither is ever a button that does nothing.
export function FooterColumn({ title, links }: FooterColumnProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  if (links.length === 0) return null;

  const titleStyle = [styles.title, { color: theme.text }];

  return (
    <div className={open ? `${css.col} ${css.open}` : css.col} style={{ borderColor: theme.border }}>
      <div className={css.title}>
        <Text style={titleStyle}>{title}</Text>
      </div>
      <div className={css.toggle}>
        <Pressable
          accessibilityRole="button"
          aria-expanded={open}
          onPress={() => setOpen((o) => !o)}
          style={styles.toggle}>
          <Text style={titleStyle}>{title}</Text>
          {/* Rotated on a wrapper: on web a transform on the SVG itself
              rotates it out of view. */}
          <View style={open ? styles.chevronOpen : undefined}>
            <ChevronDown size={18} color={theme.mutedForeground} />
          </View>
        </Pressable>
      </div>
      <div className={css.links}>
        {links.map((link) => (
          <Link key={link.key} href={link.href} style={[styles.link, { color: theme.mutedForeground }]}>
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  title: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  toggle: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 14,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  link: {
    fontSize: 14,
    lineHeight: 20,
  },
});
